/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import Route from '@ember/routing/route';
import type IntlService from 'embercom/services/intl';
import { inject as service } from '@ember/service';
import { PAGE_SIZE } from 'embercom/models/data/outbound/constants';
import { getOwner } from '@ember/application';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import AiContentLibraryApi from 'embercom/lib/ai-content-library/list-api';
import { REVIEWABLE_CONTENT_ENTITIES } from 'embercom/lib/ai-content-library/constants';
import {
  objectNames,
  objectNamesToKeys,
} from 'embercom/models/data/matching-system/matching-constants';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type Transition from '@ember/routing/transition';
import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import { EntityType } from 'embercom/models/data/entity-types';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import type FinContentReviewsController from 'embercom/controllers/apps/app/automation/fin-ai-agent/content-suggestions';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import { EditorMode } from 'embercom/components/knowledge-hub/content-editor/drawer-editor';
import { SortableFields } from 'embercom/lib/suggestions/constants';

type ModelParams = {
  searchTerm: string;
};

export default class AutomationFinAIAgentContentSuggestionsRoute extends Route {
  analytics = {
    section: 'automation',
    place: 'fin-ai-agent.content-suggestions',
  };

  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;

  async model(params: ModelParams): Promise<AiContentLibraryApi> {
    let sort_by: string;
    if (this.appService.app.canUseAiInsightsSuggestionsUi) {
      sort_by = SortableFields.Priority;
    } else {
      sort_by = 'created_at';
    }

    let api = new AiContentLibraryApi(getOwner(this), {
      object_types: this.reviewableContentEntities,
      content_wrapper_types: [ContentWrapperType.AI_CONTENT],
      app_id: this.appService.app.id as string,
      per_page: PAGE_SIZE,
      content_review_statuses: [Status.Pending],
      title: params.searchTerm,
      sort_by,
      sort_direction: 'desc',
    });

    let promises: Promise<any>[] = [api.loadPage()];
    if (await this.appService.app.canUseAiInsightsSuggestionsUi) {
      promises.push(this.knowledgeHubService.fetchAvailableLocalesFromMessengerAndHelpCenter());
    }
    await Promise.all(promises);

    this.contentSuggestionsService.listApi = api;

    return api;
  }

  setupController(
    controller: $TSFixMe,
    model: ModelFromRoute<AutomationFinAIAgentContentSuggestionsRoute>,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    let queryParams = transition.to.queryParams;
    let contentType = queryParams.content;
    let firstContentId = model.contentWrappers.firstObject?.entityId;
    let firstContentType = model.contentWrappers.firstObject?.entityType;
    if (
      this.appService.app.canUseAiInsightsSuggestionsUi &&
      model.contentWrappers.length >= 1 &&
      !queryParams.activeContentId &&
      !queryParams.activeContentType &&
      firstContentId &&
      firstContentType
    ) {
      //unregister any stale active content models
      this.knowledgeHubEditorService.registerActiveContent(undefined);

      controller.setProperties({
        activeContentId: firstContentId,
        activeContentType: objectNames[firstContentType],
        editorMode: EditorMode.REVIEW,
      });

      //ensure that the url is always updated correctly
      this.router.transitionTo({
        queryParams: {
          activeContentId: firstContentId,
          activeContentType: objectNames[firstContentType],
          editorMode: EditorMode.REVIEW,
        },
      });

      return;
    }

    if (!contentType) {
      return;
    }
    if (!this.reviewableContentEntities.includes(Number(objectNamesToKeys[contentType]))) {
      controller.setProperties({
        selectedContentType: undefined,
        selectedContentId: undefined,
      });
      this.redirectToFinContent(transition);
    }
  }

  deactivate() {
    this.contentSuggestionsService.listApi?.reset();
  }

  private redirectToFinContent(transition: Transition): void {
    this.router.transitionTo({
      queryParams: {
        ...transition.to.queryParams,
        selectedContentType: undefined,
        selectedContentId: undefined,
        content: undefined,
        id: undefined,
      },
    });
  }

  private get reviewableContentEntities(): EntityType[] {
    let reviewableContentEntities = [...REVIEWABLE_CONTENT_ENTITIES];
    if (this.appService.app.canUseAiInsightsSuggestionsUi) {
      reviewableContentEntities.push(EntityType.ArticleContent);
    }
    reviewableContentEntities.push(EntityType.ContentSectionRelationship);
    return reviewableContentEntities;
  }

  get titleToken(): string {
    return this.intl.t('ai-agent.sub-menu.content-suggestions.title');
  }

  resetController(controller: FinContentReviewsController, isExiting: boolean) {
    if (isExiting) {
      this.knowledgeHubDrawerEditorService.removeEmberStickyQueryParams(controller);
    }
  }
}
